/**
 * Provedor de idioma do backoffice.
 *
 * O conteúdo é resolvido no RENDER, não no load do módulo. É a diferença entre uma troca de
 * idioma que funciona e um seletor decorativo: copy montada em escopo de módulo congela o locale
 * do primeiro import, e nenhum `setState` faz o texto mudar depois (o `CLAUDE.md` registra esse
 * anti-pattern para `uiText()`, e o mesmo vale aqui).
 *
 * A escolha é gravada em `localStorage` e reaplicada no `<html lang>` — leitor de tela e
 * corretor do navegador usam esse atributo para escolher a pronúncia e o dicionário.
 */
import {
  createContext,
  type JSX,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { Bilingual } from './bilingual.js';
import {
  ADMIN_LOCALE_STORAGE_KEY,
  type AdminLocale,
  DEFAULT_ADMIN_LOCALE,
  resolveInitialLocale,
} from './locale.js';
import { type AdminContent, getAdminContent } from './resolve.js';

interface LocaleContextValue {
  readonly locale: AdminLocale;
  readonly setLocale: (proximo: AdminLocale) => void;
  readonly content: AdminContent;
}

/* Default coerente em vez de `null`: um componente montado fora do provedor (teste isolado,
   storybook) renderiza no idioma padrão em vez de explodir. `setLocale` é no-op ali — sem
   provedor não há onde guardar. */
const LocaleContext = createContext<LocaleContextValue>({
  locale: DEFAULT_ADMIN_LOCALE,
  setLocale: () => undefined,
  content: getAdminContent(DEFAULT_ADMIN_LOCALE),
});

function lerGuardado(): string | null {
  return window.localStorage.getItem(ADMIN_LOCALE_STORAGE_KEY);
}

function aplicarNoDocumento(locale: AdminLocale): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = locale;
}

export function AdminLocaleProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const [locale, definirLocale] = useState<AdminLocale>(() => {
    const inicial =
      typeof window === 'undefined'
        ? DEFAULT_ADMIN_LOCALE
        : resolveInitialLocale(lerGuardado, window.navigator.languages ?? []);
    aplicarNoDocumento(inicial);
    return inicial;
  });

  const setLocale = useCallback((proximo: AdminLocale) => {
    definirLocale(proximo);
    aplicarNoDocumento(proximo);
    /* Persistir é best-effort: em contexto sem `localStorage` a troca vale para a sessão, e
       perder a preferência é melhor que derrubar a tela. */
    try {
      window.localStorage.setItem(ADMIN_LOCALE_STORAGE_KEY, proximo);
    } catch {
      /* Sem armazenamento — a escolha vale só nesta sessão. */
    }
  }, []);

  const valor = useMemo<LocaleContextValue>(
    () => ({ locale, setLocale, content: getAdminContent(locale) }),
    [locale, setLocale],
  );

  return <LocaleContext.Provider value={valor}>{children}</LocaleContext.Provider>;
}

/** O idioma corrente e como trocá-lo. */
export function useAdminLocale(): LocaleContextValue {
  return useContext(LocaleContext);
}

/** Atalho para quem só quer a copy: `const { shell } = useAdminContent();` */
export function useAdminContent(): AdminContent {
  return useContext(LocaleContext).content;
}

/**
 * A copy de UMA área, no idioma corrente.
 *
 * Existe para que cada área seja auto-suficiente: o módulo da área declara o próprio
 * `Bilingual<T>` e exporta o próprio hook, sem passar por um registro central. Com ~20 áreas,
 * um `AdminContent` enumerando todas seria um arquivo que toda migração precisa editar — e
 * duas pessoas migrando telas diferentes voltariam a colidir no mesmo lugar, que é justamente
 * o que a divisão por área evita.
 *
 * Uso no módulo da área:
 *
 *     const COMPANIES: Bilingual<CompaniesContent> = { 'pt-BR': {…}, 'en-US': {…} };
 *     export const useCompaniesContent = (): CompaniesContent => useBilingual(COMPANIES);
 */
export function useBilingual<T>(area: Bilingual<T>): T {
  return area[useContext(LocaleContext).locale];
}
